/**
 * shell/shellDecl.ts —— **布局壳声明装载与闭集校验**（结构与组件树的唯一来源）。
 *
 * 依据（逐字）：
 *  ·《UI 全局布局壳规范》v1.2 —— §1／§1bis 断点｜§2 四 root 座位＋区域表｜§4 中栏与视图环
 *    ｜§5 右栏停靠｜§6 框架级浮层｜§7 设置面板 11 组项＋S1–S3＋退役禁列｜§8 卡位规则 R1–R4
 *  ·《UI 整改施工方案件》§3 关键约束 —— 结构只能由声明/投影生成；禁复列退役设置项
 *
 * 纪律：`AppShell` **只遍历本声明**渲染（禁硬编码结构）；本件**只写结构名，不写色值/间距值**
 *      （色/间距/字号一律经 `styles/tokens/xg-tokens.css`）；`views/*.yaml` **不得**决定导航结构（R1）。
 */
import yaml from "js-yaml";
import SHELL_RAW from "./shell.decl.yaml?raw";
import { E_CODES } from "../transport/errorText";

/** 座位种类（§2） */
export type SeatKind = "single" | "keyed" | "list";
/** 左栏形态（§1／§1bis 断点表） */
export type SidebarMode = "resident" | "collapsible" | "rail" | "drawer";
/** 视图环形态 */
export type ViewRingMode = "top" | "bottom" | "selector";   // selector＝手机档（§1bis 禁横向滚动 tab 条 ⇒ 收成一个位置）
/** 输入区形态 */
export type InputMode = "bottom" | "fixed_bottom";
/** 右栏形态（§5） */
export type RightbarForm = "push" | "fullscreen";
/** 可达路（§8·R2／R3；缺省 viewring）：`badge`＝待办徽标入口（动作面焦点呈现）。
 *  ★20261005 大王令：删掉自造的那个「详情进右栏」类目 ⇒ 只留 badge 例外；**P1 批不回退**。 */
export type ReachVia = "viewring" | "badge";
/** 设置项作用面（§7·S3） */
export type SettingScope = "global" | "future_sessions" | "current_session" | "read_only";

export interface SeatDecl {
  id: string; kind: SeatKind; aria: string;
  readonly?: string[]; areas?: string[];
  forms?: RightbarForm[]; collapse_cost?: string;
  reserved_key?: string; collapsed_rail?: boolean;
  pointer_events?: string; entries_opt_in?: boolean;
}

export interface BreakDecl {
  id: string; min_width: number; orientation?: "landscape";
  sidebar: SidebarMode; sidebar_overlay: boolean;
  rightbar_forms: RightbarForm[]; viewring: ViewRingMode; input: InputMode;
}

export interface ReachDecl { view: string; via: ReachVia; label?: string }

/** §3 新增会话动作声明（结构/文案唯一来源；`requires` 声明宿主须绑定真实通道） */
export interface NewSessionDecl {
  action: string;
  label: string;
  aria_label: string;
  /** 绑定要求（如 `host_binding`＝须宿主注入真实创建通道，缺绑定则禁用并说明） */
  requires: string;
  /** 无绑定时呈现的**缺席说明**（禁静默无反应） */
  absent_note: string;
}

/** §4 上行发送动作声明（结构/文案唯一来源；`requires` 声明宿主须绑定真实上行通道） */
export interface ComposerSendDecl {
  action: string;
  label: string;
  aria_label: string;
  /** 绑定要求（`host_binding`＝须宿主注入真实上行通道；缺绑定则禁用并说明，禁「假发」） */
  requires: string;
  /** 无绑定时呈现的**缺席说明**（禁静默无反应、禁清空输入假装已发） */
  absent_note: string;
}

/** §3 会话行取值口径（投影装配事实键 → `SessionItem` 键；候选键按序命中） */
export interface SessionRowSourceDecl {
  id: string[];
  title: string[];
  state: string[];
  activity: string[];
}

/**
 * §4 中栏「流」的行取值口径（★C-Ⅷ）：**载体键/数据值 → 节点行字段** 的声明承载位。
 *
 * 由来：后端投影把读面行放在**九键闭集载体**上（`fact` 闭集；同 §3 会话行口径），
 * 前端据本块还原 12 类节点行（`{key, kind, createdAt, state}`）——**禁在组件里硬编码
 * 第二份对应表**（左栏会话行同理，两处口径同族同形）。
 */
export interface FlowRowSourceDecl {
  /** 适用界面名（视图环里的界面；非该界面 ⇒ 行原样透传，不套本口径） */
  view: string;
  /** 行锚候选键（逐键命中；皆缺 ⇒ 由渲染层按行序兜锚） */
  key: string[];
  /** 判节点类的**数据字段**（载体键名，如 `actor`） */
  kind_from: string;
  /** 数据值 → 节点类（词表＝本件 `main.flow_nodes`；**值域外 ⇒ 不算节点行**） */
  kind_map: Record<string, string>;
  /** 节点行字段 ← 载体键候选（如 `{text: [text, scope]}`） */
  fields: Record<string, string[]>;
  /**
   * ★F3c：**列表型字段**（值在线上是空格分隔串，渲染件入参要数组者）——列在 `fields` 里、
   * 在此声明 ⇒ `state[name]` 拆成字符串数组（如 `attachments`：附件 `file_id` 串）。
   * 缺 ⇒ 全部按标量（原行为不变）。
   */
  list_fields?: string[];
  /** 需「定稿态」的节点类（读面行皆**已落库**件 ⇒ `state.status=settled`＋正文块） */
  settled: string[];
}

export interface SettingValueDecl { id: string; label: string }

export interface SettingGroupDecl {
  id: string; area: string; title: string; scope: SettingScope; control: string;
  values?: SettingValueDecl[]; default?: string | number;
  min?: number; max?: number; note?: string;
  action?: string; action_label?: string; items?: string[];
  groups_from?: string; options_from?: string;
}

/** §5 右栏停靠面声明（v1.2.4 取回：常显入口／待办徽标／空闲空态／覆盖形态控件／只读） */
export interface RightbarDecl {
  /** push 展开态控件位（搭在 tab 条末端） */
  controls_slot?: string;
  controls: string[];
  /** 覆盖形态（fullscreen／移动端真全屏）控件位＝**面板内**（tab 条被盖） */
  overlay_controls?: string[];
  /** 宽度手柄可见性（`push_expanded_only`＝仅普通展开态） */
  width_handle: string;
  title_row: string;
  /** 常显入口位置（如 `main.header`；A-035 起＝header **末位**／右上角） */
  entry?: string;
  /**
   * 入口徽标（A-033 去重后**只许 `none`**：待办计数唯一显示位＝`viewring.reach.via=badge`
   * 的待办入口；右栏钮不再挂同一计数 ⇒ 同一计数不得两处显示）。
   */
  entry_badge?: string;
  /** 空闲态（无选中内容）空态提示文案 */
  empty?: string;
  /** 正文是否只读（右栏＝详情面，不接上行命令） */
  readonly?: boolean;
  no_session: string;
}

export interface ShellDecl {
  version: string; spec: string;
  seats: SeatDecl[];
  regions: string[];
  breaks: BreakDecl[];
  viewring: {
    home: string; session_preference: string; source: string;
    labels_from: string; reach: ReachDecl[];
  };
  main: {
    header: string[]; flow_nodes: string[];
    /** 中栏流：行取值口径（缺 ⇒ 不套口径，行原样透传） */
    flow_row_source?: FlowRowSourceDecl;
    composer: {
      stack: string[]; input: string[]; takeover: string[]; send: ComposerSendDecl;
    };
  };
  rightbar: RightbarDecl;
  settings: { entry: string; title: string; groups: SettingGroupDecl[]; retired: string[] };
  sidebar: {
    segments: string[]; brand: { mark: string; name: string };
    new_session: NewSessionDecl; session_row: string[];
    /** 会话行取值候选键（后端产出口径的声明承载位；逐键命中即取，未命中即缺） */
    session_row_source: SessionRowSourceDecl;
    /** 会话源缺席时的**空态原因**（禁假造条目；无源即空态＋原因） */
    sessions_empty: string;
    sort: string;
    collapse: string; scrollbar: string;
  };
  mobile: {
    density_default: string; font_default: number; font_range: number[];
    topbar_h: number; tabbar_h: number; input_min_h: number; hit_target: number;
    drawer_width?: string;
    sheet_radius: number; sheet_handle: number[]; sheet_max_height_pct: number;
    overlay_opacity: number; dur_drawer_ms: number[]; dur_fade_ms: number; ease: string;
  };
  overlay: { preset_entries: unknown[] };
}

export class ShellDeclError extends Error {
  constructor(message: string) {
    super(E_CODES.SHELL_DECL + "：" + message);
    this.name = "ShellDeclError";
  }
}

const SEAT_KINDS: readonly SeatKind[] = ["single", "keyed", "list"];
const SIDEBAR_MODES: readonly SidebarMode[] = ["resident", "collapsible", "rail", "drawer"];
const VIEWRING_MODES: readonly ViewRingMode[] = ["top", "bottom", "selector"];
const INPUT_MODES: readonly InputMode[] = ["bottom", "fixed_bottom"];
const RIGHTBAR_FORMS: readonly RightbarForm[] = ["push", "fullscreen"];
const REACH_VIAS: readonly ReachVia[] = ["viewring", "badge"];
const SCOPES: readonly SettingScope[] = ["global", "future_sessions", "current_session", "read_only"];
const CONTROLS = ["radio", "range", "grouped_select", "fixed", "status_action", "readonly", "action", "view_choice"] as const;

/** 四 root 座位（§2；缺一即红） */
export const ROOT_SEATS = ["sidebar", "main", "rightbar", "shell.overlay"] as const;
/** §7 十一个设置组（DoD 3；缺一即红） */
export const SETTING_GROUP_IDS = [
  "theme", "font_size", "motion", "density", "default_model", "submit_mode",
  "busy_delivery", "connection", "about", "diagnostics", "session_view",
] as const;
/** §7 已退役设置项词表（**禁列**；出现即红） */
export const RETIRED_SETTING_TERMS = [
  "推理强度", "权限档", "排队", "会话模型", "会话权限", "cwd",
] as const;

function requireArray(node: unknown, where: string): unknown[] {
  if (!Array.isArray(node) || node.length === 0) throw new ShellDeclError(where + "：缺失或空");
  return node;
}

function groupOf(groups: SettingGroupDecl[], id: string): SettingGroupDecl {
  const found = groups.find((group) => group.id === id);
  if (!found) throw new ShellDeclError("设置缺项：" + id);
  return found;
}

/**
 * 「宿主绑定动作」声明校验（`sidebar.new_session`／`main.composer.send` 同形）：
 * 五字段齐 ⇒ 结构/文案只由声明给；缺一即红（禁静默无反应、禁假装成功）。
 */
function assertBoundAction(node: unknown, where: string): void {
  if (!node || typeof node !== "object") {
    throw new ShellDeclError(where + " 缺失（须为动作声明）");
  }
  const action = node as Record<string, unknown>;
  for (const key of ["action", "label", "aria_label", "requires", "absent_note"] as const) {
    if (typeof action[key] !== "string" || !action[key]) {
      throw new ShellDeclError(where + "." + key + " 缺失");
    }
  }
}

/** 逐件校验（形状＋闭集＋引用完整性；越集即抛——不判绿） */
export function validateShellDecl(raw: unknown): ShellDecl {
  if (!raw || typeof raw !== "object") throw new ShellDeclError("非对象");
  const doc = raw as Partial<ShellDecl>;
  if (typeof doc.version !== "string" || typeof doc.spec !== "string") {
    throw new ShellDeclError("version/spec 缺失");
  }

  const seats = requireArray(doc.seats, "seats") as SeatDecl[];
  const seatIds = new Set<string>();
  for (const seat of seats) {
    if (!SEAT_KINDS.includes(seat.kind)) throw new ShellDeclError("座位 kind 越集：" + seat.id);
    if (seatIds.has(seat.id)) throw new ShellDeclError("座位 id 重复：" + seat.id);
    seatIds.add(seat.id);
    for (const form of seat.forms ?? []) {
      if (!RIGHTBAR_FORMS.includes(form)) throw new ShellDeclError("座位 forms 越集：" + seat.id);
    }
  }
  for (const root of ROOT_SEATS) {
    if (!seatIds.has(root)) throw new ShellDeclError("缺四 root 座位之一：" + root);
  }

  const regions = requireArray(doc.regions, "regions") as string[];

  const breaks = requireArray(doc.breaks, "breaks") as BreakDecl[];
  const breakIds = new Set<string>();
  for (const item of breaks) {
    if (breakIds.has(item.id)) throw new ShellDeclError("断点重复：" + item.id);
    breakIds.add(item.id);
    if (typeof item.min_width !== "number") throw new ShellDeclError("断点缺 min_width：" + item.id);
    if (!SIDEBAR_MODES.includes(item.sidebar)) throw new ShellDeclError("左栏形态越集：" + item.id);
    if (!VIEWRING_MODES.includes(item.viewring)) throw new ShellDeclError("视图环形态越集：" + item.id);
    if (!INPUT_MODES.includes(item.input)) throw new ShellDeclError("输入区形态越集：" + item.id);
    for (const form of requireArray(item.rightbar_forms, "breaks." + item.id + ".rightbar_forms") as RightbarForm[]) {
      if (!RIGHTBAR_FORMS.includes(form)) throw new ShellDeclError("右栏形态越集：" + item.id);
    }
  }
  if (!breaks.some((b) => b.min_width === 0)) throw new ShellDeclError("断点须含兜底 min_width=0");

  const viewring = doc.viewring;
  if (!viewring || typeof viewring.home !== "string") throw new ShellDeclError("viewring.home 缺失");
  for (const reach of viewring.reach ?? []) {
    if (!REACH_VIAS.includes(reach.via)) throw new ShellDeclError("可达路越集：" + reach.view);
    if (typeof reach.view !== "string" || !reach.view) throw new ShellDeclError("可达路缺 view");
  }

  if (!doc.main || !Array.isArray(doc.main.header) || !Array.isArray(doc.main.flow_nodes)) {
    throw new ShellDeclError("main 缺失");
  }
  // ★A-035：header **末位**＝右栏展开钮（右上角锚位）——渲染序＝声明序，末位非 `rightbar_open` 即红。
  const header = doc.main.header as string[];
  if (header[header.length - 1] !== "rightbar_open") {
    throw new ShellDeclError("main.header 末位须为 rightbar_open（右上角锚位；A-035）");
  }
  if (!doc.main.composer || !Array.isArray(doc.main.composer.stack)) {
    throw new ShellDeclError("main.composer 缺失");
  }
  // §4 上行发送＝动作声明（v1.2.4）：五字段齐 ⇒ 结构/文案只由声明给；缺一即红
  assertBoundAction(doc.main.composer.send, "main.composer.send");
  // §4 接管态承载视图（v1.2.4）：等待面（审批/提问）只在本列所列视图内呈现；缺一即红
  if (!Array.isArray(doc.main.composer.takeover) || doc.main.composer.takeover.length === 0) {
    throw new ShellDeclError("main.composer.takeover 缺失（接管态承载视图须声明）");
  }
  // §4 中栏流行取值口径（★C-Ⅷ）：在位即须合法（键表非空、节点类 ∈ 本件 flow_nodes 词表——
  // 词表外节点类 ⇒ 红：否则行会被渲成 unknown，静默丢掉内容）。不在位 ⇒ 不套口径（行原样透传）。
  const flowRows = doc.main.flow_row_source as unknown as Record<string, unknown> | undefined;
  if (flowRows !== undefined) {
    if (typeof flowRows.view !== "string" || !flowRows.view) {
      throw new ShellDeclError("main.flow_row_source.view 缺失（须给适用界面名）");
    }
    if (!Array.isArray(flowRows.key) || flowRows.key.length === 0 ||
        flowRows.key.some((k) => typeof k !== "string" || !k)) {
      throw new ShellDeclError("main.flow_row_source.key 缺失或非字符串表");
    }
    if (typeof flowRows.kind_from !== "string" || !flowRows.kind_from) {
      throw new ShellDeclError("main.flow_row_source.kind_from 缺失（须给判节点类的数据字段）");
    }
    const kindMap = flowRows.kind_map;
    if (!kindMap || typeof kindMap !== "object" || Array.isArray(kindMap) ||
        Object.keys(kindMap).length === 0) {
      throw new ShellDeclError("main.flow_row_source.kind_map 缺失（数据值 → 节点类）");
    }
    const declared = new Set(doc.main.flow_nodes as string[]);
    for (const [dataValue, nodeKind] of Object.entries(kindMap as Record<string, unknown>)) {
      if (typeof nodeKind !== "string" || !nodeKind) {
        throw new ShellDeclError("main.flow_row_source.kind_map." + dataValue + " 非节点类串");
      }
      if (!declared.has(nodeKind)) {
        throw new ShellDeclError("main.flow_row_source.kind_map." + dataValue +
          " 的值不在 main.flow_nodes 词表内：" + nodeKind);
      }
    }
    const fields = flowRows.fields;
    if (!fields || typeof fields !== "object" || Array.isArray(fields) ||
        Object.keys(fields).length === 0) {
      throw new ShellDeclError("main.flow_row_source.fields 缺失（节点行字段 ← 载体键候选）");
    }
    for (const [name, candidates] of Object.entries(fields as Record<string, unknown>)) {
      if (!Array.isArray(candidates) || candidates.length === 0 ||
          candidates.some((c) => typeof c !== "string" || !c)) {
        throw new ShellDeclError("main.flow_row_source.fields." + name + " 缺失或非字符串表");
      }
    }
    const listFields = flowRows.list_fields;
    if (listFields !== undefined) {
      if (!Array.isArray(listFields) ||
          listFields.some((n) => typeof n !== "string" || !n)) {
        throw new ShellDeclError("main.flow_row_source.list_fields 须为字段名表（字符串）");
      }
      const known = new Set(Object.keys(fields as Record<string, unknown>));
      for (const name of listFields as string[]) {
        if (!known.has(name)) {
          throw new ShellDeclError("main.flow_row_source.list_fields." + name + " 不在 fields 内");
        }
      }
    }
    if (!Array.isArray(flowRows.settled) ||
        flowRows.settled.some((k) => typeof k !== "string" || !declared.has(k as string))) {
      throw new ShellDeclError("main.flow_row_source.settled 须为节点类数组（∈ 本件 flow_nodes）");
    }
  }
  // §5 右栏（v1.2.4 取回）：常显入口＋待办徽标＋空闲空态＋只读＋覆盖形态面板内控件——**缺一即红**
  if (!doc.rightbar || !Array.isArray(doc.rightbar.controls)) throw new ShellDeclError("rightbar 缺失");
  if (doc.rightbar.width_handle !== "push_expanded_only") {
    throw new ShellDeclError("rightbar.width_handle 须为 push_expanded_only");
  }
  if (!doc.rightbar.entry) throw new ShellDeclError("rightbar.entry 缺失（常显入口须给位置）");
  // ★A-033：入口徽标**只许 `none`**——待办计数唯一显示位＝待办入口（`reach.via=badge`）；
  //   右栏钮再挂同一计数 ⇒ 同一计数两处显示（大王报障「▸+0 被误读为图标」）⇒ 出现即红。
  if (doc.rightbar.entry_badge !== "none") {
    throw new ShellDeclError("rightbar.entry_badge 须为 none（待办计数只归 badge 入口；A-033）");
  }
  if (!doc.rightbar.empty) throw new ShellDeclError("rightbar.empty 缺失（空闲态须给空态提示）");
  if (doc.rightbar.readonly !== true) throw new ShellDeclError("rightbar.readonly 须为 true（正文只读）");
  if (!Array.isArray(doc.rightbar.overlay_controls) || doc.rightbar.overlay_controls.length === 0) {
    throw new ShellDeclError("rightbar.overlay_controls 缺失（覆盖形态须有面板内控件）");
  }
  if (!doc.sidebar || !Array.isArray(doc.sidebar.segments)) throw new ShellDeclError("sidebar 缺失");
  // §3 新增会话＝动作声明（v1.2.4 取回）：五字段齐 ⇒ 结构/文案只由声明给；缺一即红
  assertBoundAction(doc.sidebar.new_session, "sidebar.new_session");
  // §3 会话源缺席空态原因（v1.2.4 取回）：缺一即红（无源不得静默空列）
  if (typeof doc.sidebar.sessions_empty !== "string" || !doc.sidebar.sessions_empty) {
    throw new ShellDeclError("sidebar.sessions_empty 缺失（无源须给空态原因）");
  }
  // §3 会话行取值口径（v1.2.4 取回）：四键各须非空字符串表（缺一即红——禁在组件里硬编码第二份口径）
  const rowSource = doc.sidebar.session_row_source as unknown as Record<string, unknown> | undefined;
  if (!rowSource || typeof rowSource !== "object") {
    throw new ShellDeclError("sidebar.session_row_source 缺失（会话行取值口径须声明）");
  }
  for (const key of ["id", "title", "state", "activity"] as const) {
    const candidates = rowSource[key];
    if (!Array.isArray(candidates) || candidates.length === 0 ||
        candidates.some((item) => typeof item !== "string" || !item)) {
      throw new ShellDeclError("sidebar.session_row_source." + key + " 缺失或非字符串表");
    }
  }
  if (!doc.mobile || !Array.isArray(doc.mobile.font_range)) throw new ShellDeclError("mobile 缺失");

  const settings = doc.settings;
  if (!settings || typeof settings.title !== "string") throw new ShellDeclError("settings 缺失");
  const groups = requireArray(settings.groups, "settings.groups") as SettingGroupDecl[];
  const groupIds = new Set(groups.map((g) => g.id));
  for (const id of SETTING_GROUP_IDS) {
    if (!groupIds.has(id)) throw new ShellDeclError("设置缺项（§7 十一组）：" + id);
  }
  if (groups.length !== SETTING_GROUP_IDS.length) {
    throw new ShellDeclError("设置组数须为 " + SETTING_GROUP_IDS.length + "：现值 " + groups.length);
  }
  for (const group of groups) {
    if (!SCOPES.includes(group.scope)) throw new ShellDeclError("作用面越集：" + group.id);
    if (!(CONTROLS as readonly string[]).includes(group.control)) {
      throw new ShellDeclError("控件越集：" + group.id);
    }
    // ★A-034③（S3 声明面不变量）：`current_session` 作用面**只许** `session_view` 一项——
    //   会话级写入口唯一（`PreferencesStore.setSessionView`）；越 scope 即红另见写面。
    if (group.scope === "current_session" && group.id !== "session_view") {
      throw new ShellDeclError("current_session 作用面只许 session_view：" + group.id);
    }
    if (group.control === "radio") {
      const values = requireArray(group.values, "settings." + group.id + ".values") as SettingValueDecl[];
      for (const value of values) {
        if (typeof value.id !== "string" || typeof value.label !== "string") {
          throw new ShellDeclError("枚举值不完整：" + group.id);
        }
      }
    }
  }
  // §7 禁列：退役项**不得**以组名/标题/枚举值形式出现（出现即红）
  assertNoRetiredSettings(groups.flatMap((g) => [g.id, g.title, ...(g.values ?? []).map((v) => v.label)]));

  // §1ter·三／§7：主题＝**浅色／跟随系统**（**不提供暗色**）
  const themeIds = (groupOf(groups, "theme").values ?? []).map((v) => v.id).sort().join(",");
  if (themeIds !== "light,system") throw new ShellDeclError("主题须为「浅色／跟随系统」（不提供暗色）：" + themeIds);
  // §7：显示密度**默认紧凑**；正文字号 **12–17，默认 14**
  if (groupOf(groups, "density").default !== "compact") throw new ShellDeclError("显示密度默认须为紧凑");
  const font = groupOf(groups, "font_size");
  if (font.min !== 12 || font.max !== 17 || font.default !== 14) {
    throw new ShellDeclError("正文字号须为 12–17，默认 14");
  }

  // ★A-034①（20261006 大王令）：`settings.mechanisms` 三条说明文案**已删**——机制不自陈在 UI 上；
  //   机制本体（S1 同源／S2 无第二写路径／S3 作用面约束）由 `theme/preferences.ts` 承担并机判。
  if ("mechanisms" in (settings as unknown as Record<string, unknown>)) {
    throw new ShellDeclError("settings.mechanisms 已退役（机制提示不得复列；A-034①）");
  }
  if (!Array.isArray(settings.retired) || settings.retired.length === 0) {
    throw new ShellDeclError("settings.retired 缺失（须显式登记退役项）");
  }

  if (!doc.overlay || !Array.isArray(doc.overlay.preset_entries)) throw new ShellDeclError("overlay 缺失");
  if (doc.overlay.preset_entries.length !== 0) throw new ShellDeclError("浮层不预置示例条目（§6）");

  return doc as ShellDecl;
}

/** §7 禁列校验：渲染面出现退役词汇 ⇒ 抛（不判绿） */
export function assertNoRetiredSettings(surface: string[]): void {
  for (const text of surface) {
    for (const term of RETIRED_SETTING_TERMS) {
      if (text.includes(term)) throw new ShellDeclError("复列退役设置项：" + term + "（" + text + "）");
    }
  }
  // 「插话」须是唯一繁忙通道：出现“排队/插话二选”语义 ⇒ 红
  for (const text of surface) {
    if (text.includes("排队")) throw new ShellDeclError("复列退役设置项：繁忙投递二选（" + text + "）");
  }
}

/** 声明实例（装载即校验；不合法 ⇒ 载入期抛，不静默降级） */
export const SHELL_DECL: ShellDecl = validateShellDecl(yaml.load(SHELL_RAW));

/** 断点解算：从宽到窄取首个 `min_width ≤ width` 者（横屏手机优先命中 landscape 档） */
export function resolveBreakpoint(width: number, orientation?: "portrait" | "landscape"): BreakDecl {
  const candidates = [...SHELL_DECL.breaks].sort((a, b) => b.min_width - a.min_width);
  const landscape = orientation === "landscape" && width < 880;
  if (landscape) {
    const phone = candidates.find((b) => b.id === "phone_landscape");
    if (phone && width < 768) return phone;
  }
  for (const item of candidates) {
    if (item.orientation === "landscape") continue;
    if (width >= item.min_width) return item;
  }
  return candidates[candidates.length - 1];
}

/** 可达路（§8·R2／R3）：未列出的视图**默认 viewring**（禁使任何声明视图掉可达） */
export function reachFor(viewName: string): ReachDecl {
  return SHELL_DECL.viewring.reach.find((r) => r.view === viewName) ?? { view: viewName, via: "viewring" };
}

/** 设置组（面板与命令**读同一份事实**：S1） */
export function settingGroup(id: string): SettingGroupDecl | undefined {
  return SHELL_DECL.settings.groups.find((g) => g.id === id);
}
